import React from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { alpha, Box, Button, Chip, Grid, Link, Typography } from '@mui/material';

import { buildPremiumPlans, formatRub, PREMIUM_FEATURE_KEYS } from './premiumPlans';

const ACCENT = '#45b7d1';

function CheckRow({ children }) {
  return (
    <Box
      component="li"
      sx={{
        display: 'flex',
        alignItems: 'flex-start',
        gap: 1.25,
        py: 0.5,
        listStyle: 'none',
        color: 'text.secondary',
        fontSize: '0.9375rem',
        lineHeight: 1.45,
      }}
    >
      <Box
        aria-hidden
        sx={{
          mt: '3px',
          width: 20,
          height: 20,
          borderRadius: '50%',
          flexShrink: 0,
          bgcolor: '#ffffff',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <svg width="12" height="12" viewBox="0 0 12 12" fill="none" aria-hidden>
          <path
            d="M2.5 6.2 5.2 8.8l4.3-5.6"
            stroke="#121212"
            strokeWidth="1.7"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      </Box>
      <span>{children}</span>
    </Box>
  );
}

function PremiumCard({
  title,
  price,
  months,
  badgeLabel,
  featureKeys,
  t,
  variant,
  activePlanMonths,
  onPayPlan,
  onGoToPayment,
  paymentPendingMonths,
  paymentDisabled,
}) {
  const isAccount = variant === 'account';
  const hasActiveElsewhere = isAccount && activePlanMonths != null && activePlanMonths !== months;
  const isCurrentPlan = isAccount && activePlanMonths != null && activePlanMonths === months;
  const accentBorder = isCurrentPlan;

  const isCreatingInvoice = paymentPendingMonths === months;
  const payDisabled = (isAccount && activePlanMonths != null) || paymentDisabled || isCreatingInvoice;
  const payLabel = isCurrentPlan
    ? t('premium.currentPlan')
    : isCreatingInvoice
      ? t('premium.creatingInvoice')
      : t('premium.pay');

  return (
    <Box
      sx={{
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        p: { xs: 2.5, sm: 3 },
        borderRadius: 2,
        border: '1px solid',
        borderColor: accentBorder ? ACCENT : alpha('#fff', 0.12),
        bgcolor: alpha('#fff', accentBorder ? 0.06 : 0.03),
        boxShadow: accentBorder ? `0 0 0 1px ${alpha(ACCENT, 0.35)}` : 'none',
        opacity: hasActiveElsewhere ? 0.5 : 1,
        transition: 'opacity 0.2s ease',
      }}
    >
      <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 2, whiteSpace: 'pre-line' }}>
        {title}
      </Typography>
      <Box sx={{ mb: 0.5 }}>
        <Typography component="span" variant="h4" sx={{ fontWeight: 800, letterSpacing: '-0.02em' }}>
          {formatRub(price)}
        </Typography>
        <Typography component="span" variant="body1" color="text.secondary" sx={{ ml: 1 }}>
          ₽
        </Typography>
      </Box>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
        {t(`premium.period${months}`)}
      </Typography>
      <Box
        sx={{
          minHeight: 40,
          mb: 2,
          display: 'flex',
          alignItems: 'center',
        }}
      >
        {badgeLabel ? (
          <Chip
            label={badgeLabel}
            size="small"
            sx={{
              height: 'auto',
              maxWidth: '100%',
              fontWeight: 600,
              fontSize: '0.75rem',
              lineHeight: 1.25,
              bgcolor: ACCENT,
              color: '#0a1620',
              '& .MuiChip-label': {
                px: 1.25,
                py: 0.75,
                whiteSpace: 'normal',
                textAlign: 'left',
                display: 'block',
              },
            }}
          />
        ) : null}
      </Box>
      <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 700 }}>
        {t('premium.featuresTitle')}
      </Typography>
      <Box component="ul" sx={{ m: 0, p: 0, flex: 1 }}>
        {featureKeys.map((key) => (
          <CheckRow key={key}>{t(`premium.features.${key}`)}</CheckRow>
        ))}
      </Box>
      {isAccount ? (
        <Box sx={{ mt: 2 }}>
          <Button
            fullWidth
            variant="contained"
            disabled={payDisabled}
            onClick={(e) => {
              e.preventDefault();
              onPayPlan?.(months);
            }}
            sx={{
              borderRadius: 999,
              py: 1.25,
              textTransform: 'none',
              fontWeight: 600,
              fontSize: '1rem',
              bgcolor: '#ffffff',
              color: '#121212',
              '&:hover': { bgcolor: alpha('#fff', 0.92) },
              '&.Mui-disabled': {
                bgcolor: alpha('#fff', 0.12),
                color: alpha('#fff', 0.5),
              },
            }}
          >
            {payLabel}
          </Button>
          <Typography
            component="div"
            variant="caption"
            color="text.secondary"
            sx={{ display: 'block', mt: 1.5, lineHeight: 1.5 }}
          >
            <Trans
              i18nKey="premium.offerConsent"
              components={{
                offer: (
                  <Link
                    component="a"
                    href="/web-offer"
                    target="_blank"
                    rel="noopener noreferrer"
                    color="inherit"
                    sx={{ fontWeight: 600, textDecoration: 'underline', textUnderlineOffset: 2 }}
                  />
                ),
              }}
            />
          </Typography>
        </Box>
      ) : (
        <Box sx={{ mt: 2 }}>
          <Button
            fullWidth
            variant="contained"
            onClick={(e) => {
              e.preventDefault();
              onGoToPayment?.();
            }}
            sx={{
              borderRadius: 999,
              py: 1.25,
              textTransform: 'none',
              fontWeight: 600,
              fontSize: '1rem',
              bgcolor: '#ffffff',
              color: '#121212',
              '&:hover': { bgcolor: alpha('#fff', 0.92) },
            }}
          >
            {t('premium.goToPayment')}
          </Button>
        </Box>
      )}
    </Box>
  );
}

/**
 * @param {'home' | 'account'} props.variant
 * @param {null | 3 | 6 | 12} props.activePlanMonths — текущий оплаченный период; подсветка и блокировка кнопок на account
 */
export default function PremiumPricingBlock({
  variant = 'home',
  activePlanMonths = null,
  onPayPlan = null,
  onGoToPayment = null,
  paymentPendingMonths = null,
  paymentDisabled = false,
}) {
  const { t } = useTranslation();
  const plans = buildPremiumPlans(t);
  const featureKeys = PREMIUM_FEATURE_KEYS;

  return (
    <>
      <Typography variant="h4" sx={{ fontWeight: 800, mb: 1, textAlign: { xs: 'left', md: 'center' } }}>
        {t('premium.headline')}
      </Typography>
      <Typography
        variant="body1"
        color="text.secondary"
        sx={{
          mb: 4,
          maxWidth: 560,
          mx: { md: 'auto' },
          textAlign: { xs: 'left', md: 'center' },
          lineHeight: 1.6,
        }}
      >
        {t('premium.subtitle')}
      </Typography>
      <Grid container spacing={2.5}>
        {plans.map((plan) => (
          <Grid key={plan.months} item xs={12} md={4}>
            <PremiumCard
              title={plan.title}
              price={plan.price}
              months={plan.months}
              badgeLabel={plan.badgeLabel}
              featureKeys={featureKeys}
              t={t}
              variant={variant}
              activePlanMonths={activePlanMonths}
              onPayPlan={onPayPlan}
              onGoToPayment={onGoToPayment}
              paymentPendingMonths={paymentPendingMonths}
              paymentDisabled={paymentDisabled}
            />
          </Grid>
        ))}
      </Grid>
    </>
  );
}
